import React, { useEffect, useState, useRef } from "react";
import secureAxios from "../../../utils/secureAxios";
import { useAuth } from "../../../contexts/AuthContext";
import WidgetCard from "./WidgetCard";

const ConsumerCountWidget = ({ onlyMyData }) => {
  const { user } = useAuth();
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const hasFetchedRef = useRef(false);
  const lastParamsRef = useRef(null);

  useEffect(() => {
    const fetchCount = async () => {
      if (!user?.user_id || !user?.tenant_id) {
        setLoading(false);
        return;
      }

      const paramsKey = `${user.user_id}-${user.tenant_id}-${onlyMyData}`;

      if (hasFetchedRef.current && lastParamsRef.current === paramsKey) {
        return;
      }

      hasFetchedRef.current = true;
      lastParamsRef.current = paramsKey;
      setLoading(true);

      try {
        const res = await secureAxios.get("/api/consumers/count", {
          headers: {
            "X-Only-My-Data": onlyMyData ? "true" : "false",
          },
          params: {
            only_my_data: onlyMyData,
          },
        });

        setCount(res.data.count || 0);
        setError(false);
      } catch (err) {
        console.error("Error fetching consumer count:", err);
        if (err.response) {
          console.error("Error response:", err.response.data);
          console.error("Error status:", err.response.status);
        }
        setError(true);
        setCount(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCount();
  }, [user?.user_id, user?.tenant_id, onlyMyData]);

  // Helper to determine what type of count this is
  const getCountLabel = () => {
    if (!user) return "Active Consumer Count";

    const role = user.role?.toLowerCase() || "";
    const isAdmin = user.is_admin || false;

    if (isAdmin) {
      return onlyMyData ? "My Active Consumers" : "All Active Consumers";
    } else if (role === "counselor") {
      return "My Active Consumers";
    } else if (role === "job coach") {
      return "My Active Consumers";
    }

    return "Active Consumer Count";
  };

  // Get descriptive text
  const getDescriptionText = () => {
    if (!user) return "";

    const role = user.role?.toLowerCase() || "";
    const isAdmin = user.is_admin || false;

    if (isAdmin && !onlyMyData) {
      return "All active consumers in the system";
    } else if (isAdmin && onlyMyData) {
      return "Active consumers you directly manage";
    } else if (role === "counselor") {
      return "Your assigned active consumers";
    } else if (role === "job coach") {
      return "Active consumers you're coaching";
    }

    return "Active consumers";
  };

  return (
    <WidgetCard
      title={getCountLabel()}
      subtitle={onlyMyData !== undefined ? getDescriptionText() : undefined}
      icon="bi-people"
      iconColor="purple"
      loading={loading}
      error={error}
      errorMessage="Unable to load data"
      className="h-100"
    >
      {/* Large count display */}
      <div className="text-center py-4">
        <div className="widget-display-number text-purple mb-2">
          {count || 0}
        </div>
        <div className="text-muted">
          {count === 1 ? "Active Consumer" : "Active Consumers"}
        </div>
      </div>

      {/* Additional info for context */}
      {user?.role === "job coach" && count === 0 && (
        <div className="alert alert-info border-0 rounded-3 mb-0 py-2 px-3">
          <small>
            <i className="bi bi-info-circle me-1"></i>
            No active consumers currently assigned to you
          </small>
        </div>
      )}

      {/* Status indicator that excludes archived/inactive */}
      <div className="widget-footer">
        <small className="text-muted d-flex align-items-center justify-content-center">
          <i className="bi bi-check-circle me-1"></i>
          Excludes inactive consumers
        </small>
      </div>
    </WidgetCard>
  );
};

export default ConsumerCountWidget;
